import i18next from "i18next";
import type { InfiniteHitsCache } from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import { createInfiniteHitsSessionStorageCache } from "instantsearch.js/es/lib/infiniteHitsCache";

import type { AlgoliaSearchResult } from "@/models/AlgoliaSearchResult";
import { getAlgoliaSearchIndexName } from "@/utils/algoliaSearchClient";
import { convertProductTypeToListingType, type ListingTypeOptions } from "@/utils/productTypes";
import type { Models } from "api/types";
import type { ListingSortOptions } from "pages/root/shop/listingFilterOptions";
import { getDiscountCustomColor, getDiscountText } from "services/ListingHelperService";

/**
 * The `productType` facet values the index stores (see `ProductTypeConstants` in
 * `RepeatMD.Infrastructure.Search`).
 */
export const ALGOLIA_PRODUCT_TYPES = {
  treatment: "treatment",
  package: "product bundle",
  membership: "membership",
  giftCard: "gift card",
  retailProduct: "retail product",
} as const;

/** Which page issued a query, so Algolia Analytics can be sliced per surface. */
export type SearchSurface =
  | "search"
  | "treatments"
  | "packages"
  | "memberships"
  | "concern"
  | "category"
  | "rail";

export const buildAnalyticsTags = (
  platformId: string,
  surface: SearchSurface,
  extra: readonly string[] = [],
): string[] => [`platform:${platformId}`, `surface:${surface}`, ...extra];

export type CatalogVisibility = {
  /** `GET /retailproducts/are-retail-products-visible` — in-store ("MyProducts") retail. */
  areRetailProductsVisible: boolean | undefined;
  /** `GET /skindrop/is-visible-on-marketplace`. */
  isSkinDropVisible: boolean | undefined;
};

export type CatalogFilterInput = {
  platformId: string;
  /** The patient's current location; absent for a visitor who has not picked one yet. */
  platformLocationId?: string | null;
  visibility: CatalogVisibility;
  /** Restrict to these `productType` facet values; every type when omitted. */
  productTypes?: readonly string[];
  concernId?: string;
  categoryId?: string;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a route or API value is a UUID — the only shape an id may take inside a filter. */
export const isUuid = (value: string): boolean => UUID_PATTERN.test(value);

/** A filter literal: quoted, with the quote and escape characters escaped. */
const quoteFilterValue = (value: string): string => `"${value.replace(/["\\]/g, "\\$&")}"`;

/**
 * An id that reaches a filter from a route param or an API answer: anything that is not a UUID is
 * refused rather than interpolated, so `x OR platformId:other` can never widen a query.
 */
const assertUuid = (name: string, value: string): string => {
  if (!isUuid(value)) {
    throw new Error(`Algolia catalog filter: ${name} is not a UUID.`);
  }
  return value;
};

/**
 * The sale-window clause: records carry `availableFrom` / `availableUntil` as Unix seconds (open
 * ends as sentinels), so a gift card leaves the shop the minute its window closes rather than at the
 * nightly reindex. Now is rounded down to the minute so a filter string, and any query key or
 * InstantSearch state built from it, changes at most once a minute.
 */
export const availabilityClause = (nowMs: number): string => {
  const now = Math.floor(nowMs / 60_000) * 60;
  return `availableFrom <= ${now} AND availableUntil >= ${now}`;
};

const AVAILABILITY_CLAUSE_PATTERN = /availableFrom <= \d+ AND availableUntil >= \d+/g;

/**
 * The pages "Load more" added, kept in session storage so a list restores them on the way back from a PDP. The
 * library cache only reuses pages for an identical search, and a remount rebuilds the availability clause at a later
 * minute, so that clause is left out of the comparison.
 *
 * Pages stored before this page load are never restored: the connector renders a restored page instead of the
 * fresh response for it, so a refresh would keep showing cards from before a catalog edit.
 */
export const createListingHitsCache = (key: string): InfiniteHitsCache<AlgoliaSearchResult> => {
  // The session cache stores whatever hits it is handed as JSON; its typing just defaults to `BaseHit`.
  const cache = createInfiniteHitsSessionStorageCache({
    key,
  }) as unknown as InfiniteHitsCache<AlgoliaSearchResult>;
  type SearchState = Parameters<InfiniteHitsCache["read"]>[0]["state"];
  const withoutClock = (state: SearchState): SearchState => ({
    ...state,
    filters: state.filters?.replace(AVAILABILITY_CLAUSE_PATTERN, ""),
  });
  let hasWrittenThisPageLoad = false;
  return {
    read: ({ state }) =>
      hasWrittenThisPageLoad ? cache.read({ state: withoutClock(state) }) : null,
    write: ({ state, hits }) => {
      hasWrittenThisPageLoad = true;
      cache.write({ state: withoutClock(state), hits });
    },
  };
};

/**
 * The tenancy + visibility filter every catalog query carries. `isVisibleInShop:true` is what keeps a
 * record that has not been re-indexed since record v2 (no `isVisibleInShop` at all) out of every
 * result, so a stale record can never render. Every interpolated id is validated as a UUID and
 * quoted.
 */
export const buildCatalogFilters = (
  {
    platformId,
    platformLocationId,
    visibility,
    productTypes,
    concernId,
    categoryId,
  }: CatalogFilterInput,
  nowMs: number = Date.now(),
): string => {
  const clauses = [
    `platformId:${quoteFilterValue(assertUuid("platformId", platformId))}`,
    "isVisibleInShop:true",
    availabilityClause(nowMs),
  ];

  if (platformLocationId) {
    const locationId = quoteFilterValue(assertUuid("platformLocationId", platformLocationId));
    clauses.push(`(locationIds:${locationId} OR allLocations:true)`);
  }
  if (!visibility.isSkinDropVisible) {
    clauses.push("NOT requires.skinDrop:true");
  }
  if (!visibility.areRetailProductsVisible) {
    clauses.push("NOT requires.myProducts:true");
  }
  if (productTypes) {
    clauses.push(
      productTypes.length > 0
        ? `(${productTypes.map((type) => `productType:${quoteFilterValue(type)}`).join(" OR ")})`
        : "NOT isVisibleInShop:true",
    );
  }
  if (concernId) {
    clauses.push(`concernIds:${quoteFilterValue(assertUuid("concernId", concernId))}`);
  }
  if (categoryId) {
    clauses.push(`categoryIds:${quoteFilterValue(assertUuid("categoryId", categoryId))}`);
  }

  return clauses.join(" AND ");
};

/** A listing discount the shop shows as a sale: it takes something off, and it is not a sign-up reward. */
const isSaleDiscount = (discount: Models<"ShopProductDiscountModel"> | undefined) =>
  !!discount && !discount.isSignUpReward && !!(discount.maxPercentageOff || discount.maxAmountOff);

/**
 * The listings and memberships on sale for this patient, from `GET /shop/discounts`: exactly what today's shop
 * treats as on sale (`useListingFilterAndSort`, where a custom category's memberships carried their membership
 * discount). Promotions change by the minute and per patient, so they are never read
 * from the index.
 */
export const onSaleDiscounts = (
  shopDiscounts: Models<"ShopDiscountModel"> | undefined,
): Map<string, Models<"ShopProductDiscountModel">> =>
  new Map(
    [
      ...Object.entries(shopDiscounts?.listingDiscounts ?? {}),
      ...Object.entries(shopDiscounts?.membershipDiscounts ?? {}),
    ].filter(
      (entry): entry is [string, Models<"ShopProductDiscountModel">] =>
        isUuid(entry[0]) && isSaleDiscount(entry[1]),
    ),
  );

/** The filter clause that scopes a query to these listings ("On sale", recommendations); ids are validated and quoted. */
export const objectIdFilter = (listingIds: readonly string[]): string =>
  `(${listingIds.map((id) => `objectID:${quoteFilterValue(assertUuid("listingId", id))}`).join(" OR ")})`;

/**
 * Today's "On sale" order: the largest percentage off first, then the largest cash amount off, then the rest
 * in the order they arrived.
 */
export const sortByDiscount = <T extends Pick<AlgoliaSearchResult, "objectID">>(
  hits: readonly T[],
  discounts: ReadonlyMap<string, Models<"ShopProductDiscountModel">>,
): T[] => {
  const percentOff = (hit: T) => discounts.get(hit.objectID)?.maxPercentageOff ?? 0;
  const amountOff = (hit: T) => discounts.get(hit.objectID)?.maxAmountOff ?? 0;
  return [...hits].sort((a, b) => percentOff(b) - percentOff(a) || amountOff(b) - amountOff(a));
};

/**
 * Maps a sort option to the index that ranks it. "On sale" reads the primary index scoped to the on-sale
 * listings (`objectIdFilter`) and orders them by discount on the page (`sortByDiscount`).
 */
export const resolveSortIndexName = (sort: ListingSortOptions, envName: string): string => {
  const primary = `${envName}_search`;

  switch (sort) {
    case "bestsellers":
      return `${primary}_best_selling`;
    case "priceLowToHigh":
      return `${primary}_price_asc`;
    case "priceHighToLow":
      return `${primary}_price_desc`;
    case "recentlyAdded":
      return `${primary}_recent`;
    case "onsale":
    case "featured":
      return primary;
  }
};

/**
 * A record v2 hit: everything a card renders is on it. The narrowing is deliberate — a hit that has
 * not been re-indexed with record v2 (`isVisibleInShop` absent) fails `isCatalogHit` and is never
 * rendered, which is the fail-closed half of the `isVisibleInShop:true` filter.
 */
export type CatalogHit = AlgoliaSearchResult & {
  isVisibleInShop: true;
  pdpPath: string;
  packageIds: string[];
};

export const isCatalogHit = (hit: AlgoliaSearchResult): hit is CatalogHit =>
  hit.isVisibleInShop === true && typeof hit.pdpPath === "string" && Array.isArray(hit.packageIds);

/**
 * Which member price applies to the patient, from `GET /shop/member-pricing` — the rule the shop's listing
 * prices use, decided on the server.
 */
export type MemberPricingContext = Pick<
  Models<"DisplayMemberPricingContext">,
  "membershipIds" | "visitorPricingLocationId"
>;

/**
 * The member price a card shows. A member is priced only by the memberships they hold (the cheapest if several
 * discount the item, none if none do — a visitor price is never theirs). A patient holding no membership sees the
 * "what a member would pay" price for their pricing location; an anonymous visitor at a practice without anonymous
 * checkout has no pricing location and sees none. Until the context loads, no member price is shown.
 */
export const pickMemberPrice = (
  hit: Pick<AlgoliaSearchResult, "memberPrices" | "bestMemberPriceByLocation">,
  memberPricing: MemberPricingContext | undefined,
): number | null => {
  if (!memberPricing) {
    return null;
  }

  if (memberPricing.membershipIds.length > 0) {
    const held = memberPricing.membershipIds
      .map((id) => hit.memberPrices?.[id])
      .filter((price): price is number => typeof price === "number");
    return held.length > 0 ? Math.min(...held) : null;
  }

  const locationId = memberPricing.visitorPricingLocationId;
  const visitorPrice = locationId ? hit.bestMemberPriceByLocation?.[locationId] : undefined;
  return typeof visitorPrice === "number" ? visitorPrice : null;
};

/**
 * Retail variant member prices from `GET /memberships/discount-prices`: the rule the retail grid and the product
 * page price members with.
 */
export type RetailMemberPrices = readonly Models<"MembershipPackageDiscount">[];

/**
 * A retail card's member price, exactly as the retail grid shows it: the member price of the product's cheapest
 * variant (the one priced at the record's `minBasePrice`). Retail records carry no member prices, so until these
 * load no member price is shown.
 */
export const pickRetailMemberPrice = (
  hit: Pick<CatalogHit, "packageIds" | "minBasePrice">,
  retailMemberPrices: RetailMemberPrices | undefined,
): number | null => {
  const variantIds = new Set(hit.packageIds);
  const prices = (retailMemberPrices ?? [])
    .filter(
      (price) =>
        variantIds.has(price.packageId) && price.originalAmountInCents === hit.minBasePrice,
    )
    .map((price) => price.discountedAmountInCents);
  return prices.length > 0 ? Math.min(...prices) : null;
};

/** Custom categories are curated: hits render in the inclusion order the admin chose. */
export const sortByCategoryOrder = <T extends Pick<AlgoliaSearchResult, "categoryOrder">>(
  hits: readonly T[],
  categoryId: string,
): T[] => {
  const position = (hit: T) => hit.categoryOrder?.[categoryId] ?? Number.MAX_SAFE_INTEGER;
  return [...hits].sort((a, b) => position(a) - position(b));
};

export type HitDiscountContext = {
  /** The patient's discounts from `GET /shop/discounts`; badges appear once it loads. */
  shopDiscounts: Models<"ShopDiscountModel"> | undefined;
  customShopCategories: Models<"CategoryListingsResult">[] | undefined;
  /** Package ids inside a live free-gift promotion (`getFreeGiftPackageIds`). */
  freeGiftPackageIds: ReadonlySet<string>;
};

export type HitDiscountPresentation = {
  discountText: string;
  discount: Models<"ShopProductDiscountModel"> | null;
  customColor: string | null;
};

/**
 * Discount badge, colour and data for a hit, from the patient's discounts only — promotions are not on the
 * record, so a hit shows no badge until `GET /shop/discounts` has loaded. Sign-up rewards are never badged,
 * exactly as `getListingsWithDiscountInformation` hides them today.
 */
export const getHitDiscountPresentation = (
  hit: CatalogHit,
  type: ListingTypeOptions,
  { shopDiscounts, customShopCategories, freeGiftPackageIds }: HitDiscountContext,
): HitDiscountPresentation => {
  const overlay =
    type === "Membership"
      ? shopDiscounts?.membershipDiscounts?.[hit.objectID]
      : shopDiscounts?.listingDiscounts?.[hit.objectID];
  const overlayDiscount = overlay?.isSignUpReward ? undefined : overlay;

  const discount = overlayDiscount ?? null;

  const isFreeGift =
    type !== "Membership" && hit.packageIds.some((packageId) => freeGiftPackageIds.has(packageId));
  const discountText = isFreeGift
    ? i18next.t("listingHelperService.freeGift")
    : getDiscountText(discount ?? undefined, type === "Membership" ? "Membership" : undefined);

  const customColor = overlayDiscount
    ? (getDiscountCustomColor(overlayDiscount, customShopCategories) ?? null)
    : null;

  return { discountText, discount, customColor };
};

/** The listing type a card needs; SkinDrop retail is told apart by what the record requires. */
export const catalogHitListingType = (
  hit: Pick<AlgoliaSearchResult, "productType" | "requires">,
): ListingTypeOptions => {
  const type = convertProductTypeToListingType(hit.productType);
  return type === "Product" && hit.requires?.skinDrop ? "ProductFromExternalSource" : type;
};

/** Whether a hit is a retail product, whose card is priced by `pickRetailMemberPrice`. */
export const isRetailHit = (
  hit: Pick<AlgoliaSearchResult, "productType" | "requires">,
): boolean => {
  const type = catalogHitListingType(hit);
  return type === "Product" || type === "ProductFromExternalSource";
};

export type CatalogCardContext = HitDiscountContext & {
  /** Undefined while `GET /shop/member-pricing` loads; member prices fill in when it arrives. */
  memberPricing: MemberPricingContext | undefined;
  /** Undefined while `GET /memberships/discount-prices` loads, and on pages that never show a retail product. */
  retailMemberPrices: RetailMemberPrices | undefined;
};

const cardMemberPrice = (
  hit: CatalogHit,
  type: ListingTypeOptions,
  context: CatalogCardContext,
): number | null => {
  switch (type) {
    case "Membership":
      return null;
    case "Product":
    case "ProductFromExternalSource":
      return pickRetailMemberPrice(hit, context.retailMemberPrices);
    default:
      return hit.databaseMemberPrice !== undefined
        ? hit.databaseMemberPrice
        : pickMemberPrice(hit, context.memberPricing);
  }
};

export type CatalogCard = HitDiscountPresentation & {
  id: string;
  title: string;
  description: string;
  imageUrl: string | null;
  type: ListingTypeOptions;
  concerns: string[];
  price: number;
  memberPrice: number | null;
  unitType: string | null;
  hasLinearPricing: boolean;
  pdpPath: string;
};

/** Everything `ProductCardLarge` / `ProductCarousel` render, from a hit plus the per-patient overlay. */
export const toCatalogCard = (hit: CatalogHit, context: CatalogCardContext): CatalogCard => {
  const type = catalogHitListingType(hit);

  return {
    id: hit.objectID,
    title: hit.title,
    description: hit.description,
    imageUrl: hit.imageUrl ?? null,
    type,
    concerns: hit.concernNames,
    price: hit.minBasePrice,
    memberPrice: cardMemberPrice(hit, type, context),
    unitType: hit.unitName ?? null,
    hasLinearPricing: hit.hasLinearPricing,
    pdpPath: hit.pdpPath,
    ...getHitDiscountPresentation(hit, type, context),
  };
};

/**
 * The attributes `isCatalogHit` and `toCatalogCard` read (`objectID` always comes back), for card queries that
 * should not download whole records.
 */
export const CATALOG_CARD_ATTRIBUTES: (keyof AlgoliaSearchResult)[] = [
  "title",
  "description",
  "imageUrl",
  "productType",
  "requires",
  "concernNames",
  "minBasePrice",
  "unitName",
  "hasLinearPricing",
  "pdpPath",
  "packageIds",
  "isVisibleInShop",
  "memberPrices",
  "bestMemberPriceByLocation",
];

export const getPrimarySearchIndexName = () => getAlgoliaSearchIndexName();

export const getBestSellingIndexName = () => getAlgoliaSearchIndexName("search_best_selling");
